import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout); 
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const quantitySelector = /** @type {any} */ (form.querySelector('quantity-selector-component'));
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }

    this.animateAddToCart();

    const animationEnabled = this.dataset.addToCartAnimation === 'true';

    if (animationEnabled && !event.target.closest('.quick-add-modal')) {
      this.#animateFlyToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {AbortController | undefined} */
  #priceUpdateAbortController;

  /** @type {Record<string, string>} */
  static #colorMap = {
    'Black': '#000000',
    'White': '#FFFFFF',
    'Navy': '#1A2B4A',
    'Red': '#C41E3A',
    'Royal': '#4169E1',
    'Grey': '#808080',
    'Light Grey': '#D3D3D3',
    'Dark Grey Heather': '#4A4A4A',
    'Kelly Green': '#4CBB17',
    'Forest Green': '#228B22',
    'Olive': '#808000',
    'Charcoal': '#36454F',
    'Khaki': '#C3B091',
    'Camo': 'camo',
    'Old Mauve': '#8B4558',
    'Yellow': '#F5C518',
    'Green': '#3A7D44',
    'Police Blue': '#1A3A5C',
    'Purple': '#6B3FA0',
    'Indigo': '#3D3B8E',
    'Orange Gold': '#DAA520',
    'Denim': '#1560BD',
    'Navy Blue': '#000080',
    'Teal Blue': '#008080',
    'Cerulean Blue': '#007BA7',
    'Mint Green': '#98FF98',
    'Deep Fuschia': '#C154C1',
    'Pink': '#FFC0CB',
    'Oak Brown': '#806517',
    'Orange': '#FFA500',
    'Yellow Green': '#9ACD32',
    'Lime Green': '#32CD32',
    'Aqua blue': '#00FFFF',
    'Royal Blue': '#4169E1',
    'Eucalyptus': '#44D7A8',
    'Purple Haze': '#8A2BE2',
    'Twilight Blue': '#2E5A88',
    'Cocoa': '#D2691E',
    'Turmeric': '#FFCC00',
    'Wine Red': '#722F37',
    'Pale Orange': '#FFB347',
    'Sunny Yellow': '#FFD700',
    'Blueberry': '#4B0082',
    'Jade Green': '#00A86B',
    'Dark Red': '#8B0000',
    'Apricot': '#FBCEB1',
    'Gravel': '#4A4A4A',
    'Antique Brass': '#B5A642',
    'Forest Green Lavender': '#5F8D4E',
    'Faded Orange': '#E8A87C',
    'Turquoise': '#40E0D0',
    'County Green': '#2E8B57',
    'Dark Purple': '#4B0082',
    'Army Green': '#4B5320',
    'Army Grey': '#6B6B6B',
    'Army Twilight': '#4A4E69',
    'Army Lavender': '#9D8189',
    'Army Wedgewood': '#5C7C8A',
    'Army Chestnut': '#954535',
    'Blue Ivy': '#30D5C8',
  };

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, /** @type {EventListener} */ (/** @type {unknown} */ (this.#onVariantUpdate)), { signal });
    target?.addEventListener(ThemeEvents.variantSelected, /** @type {EventListener} */ (/** @type {unknown} */ (this.#onVariantSelected)), { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, /** @type {EventListener} */ (/** @type {unknown} */ (this.#onCartUpdate)), { signal });

    if (this.#isCustomConfigurator()) {
      this.#initCustomConfigurator();
    } else if (this.#isCustomIndividualConfigurator()) {
      this.#initCustomIndividualConfigurator();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Fetches cart and updates quantity selector for current variant
   * @returns {Promise<number>} The cart quantity for the current variant
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return 0;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      const cartItem = cart.items.find(
        /** @param {any} item */
        (item) => item.variant_id.toString() === variantIdInput.value.toString()
      );
      const cartQty = cartItem ? cartItem.quantity : 0;

      // Use public API to update quantity selector
      const quantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
      if (quantitySelector?.setCartQuantity) {
        quantitySelector.setCartQuantity(cartQty);
      }

      // Update quantity label if it exists
      this.#updateQuantityLabel(cartQty);

      return cartQty;
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return 0;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    await this.#fetchAndUpdateCartQuantity();
  };

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  async handleSubmit(event) {
    const { addToCartTextError, addToCartButtonContainer } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Check if the add to cart button is disabled and do an early return if it is
    if (addToCartButtonContainer?.refs.addToCartButton?.disabled) return;

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    const formData = new FormData(form);
    const moqInput = /** @type {HTMLInputElement | null} */ (form.querySelector('.input_moq[name="quantity"]'));
    const quantity = moqInput
      ? Number(moqInput.value) || Number(this.dataset.quantityDefault) || 1
      : Number(formData.get('quantity')) || Number(this.dataset.quantityDefault) || 1;
    // console.log('[product-form] moqInput:', moqInput, 'value:', moqInput?.value, 'quantity:', quantity, 'formData quantity:', formData.get('quantity'));

    const items = [
      {
        id: formData.get('id'),
        quantity,
        properties: /** @type {Record<string, string>} */ ({}),
      },
    ];

    for (const [key, value] of formData.entries()) {
      if (key.startsWith('properties[')) {
        const propertyMatch = key.match(/^properties\[(.+)\]$/);
        const propertyName = propertyMatch?.[1];
        if (propertyName && items[0]?.properties) {
          items[0].properties[propertyName] = /** @type {string} */ (value);
        }
      }
    }

    // Add product_type property for custom-caps-individual products for delivery sync
    if (this.dataset.templateSuffix === 'custom-caps-individual' && items[0]?.properties) {
      items[0].properties.product_type = 'custom-cap';
      
      // Add variant ID for delivery sync tracking
      const variantId = /** @type {string} */ (formData.get('id')) || '';
      if (variantId) {
        items[0].properties.cap_variant_id = variantId;
      }

      // Add selected color from color swatches (not a variant option)
      const colorValue = this.querySelector('[data-color-value]')?.textContent;
      if (colorValue) {
        items[0].properties['Color'] = colorValue.trim();
      }

      // Add delivery method as property (not a variant option)
      const deliverySelect = /** @type {HTMLSelectElement | null} */ (this.querySelector('select[name="delivery-method"]'));
      if (deliverySelect?.value) {
        items[0].properties['Delivery'] = deliverySelect.value;
      }
    }

    const addonSelects = this.querySelectorAll('select[name="addon"]');
    addonSelects.forEach((select) => {
      const selectElement = /** @type {HTMLSelectElement} */ (select);
      if (!selectElement.value) return;
      items.push({
        id: selectElement.value,
        quantity,
        properties: {},
      });
    });

    const deliverySelects = this.querySelectorAll('select[name="delivery"]');
    if (deliverySelects.length > 0) {
      // Use variant ID for tracking (always available)
      const variantId = /** @type {string} */ (formData.get('id')) || '';
      
      // Try to get SKU for display purposes
      const variantSelect = this.querySelector('select[name="id"]');
      let capSku = '';
      
      if (variantSelect) {
        const selectedOption = variantSelect.querySelector(`option[value="${variantId}"]`);
        if (selectedOption) {
          capSku = selectedOption.getAttribute('data-sku') || '';
        }
      }
      
      // Fallback to form input if not found
      if (!capSku) {
        capSku = /** @type {string} */ (formData.get('sku')) || '';
      }

      // Check if delivery already exists for this specific cap variant (by variant ID)
      const cartRes = await fetch('/cart.js');
      /** @type {{ items: { handle: string; properties?: Record<string, string> }[] }} */
      const cart = await cartRes.json();
      const hasDeliveryForVariant = cart.items.some(
        (item) => item.handle === 'cap-delivery' && item.properties?.cap_variant_id === variantId
      );

      if (!hasDeliveryForVariant) {
        deliverySelects.forEach((select) => {
          const selectElement = /** @type {HTMLSelectElement} */ (select);
          if (!selectElement.value) return;
          items.push({
            id: selectElement.value,
            quantity: 1,
            properties: {
              cap_variant_id: variantId,
              cap_sku: capSku, // For display only
            },
          });
        });
      }
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = /** @type {string[]} */ ([]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });

    const body = {
      items,
      sections: cartItemComponentsSectionIds.join(','),
    };
    // console.log('[cart/add] request body:', JSON.stringify(body, null, 2));

    const fetchCfg = fetchConfig('json', { body: JSON.stringify(body) });

    fetch('/cart/add.js', {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'application/json',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        // console.log('[cart/add] response:', response);
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = response.items?.[0]?.variant_id;

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // Fetch the updated cart to get the actual total quantity for this variant
          this.#fetchAndUpdateCartQuantity();

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    // Update the variant ID
    variantId.value = event.detail.resource?.id ?? '';

    if (!currentAddToCartButton && !this.refs.acceleratedCheckoutButtonContainer) return;

    // Update the button state
    if (currentAddToCartButton) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        addToCartButtonContainer.disable();
      } else {
        addToCartButtonContainer.enable();
      }

      // Update the add to cart button text and icon
      if (newAddToCartButton) {
        morph(currentAddToCartButton, newAddToCartButton);
      }
    }

    if (this.refs.acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const quantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(
        newQuantityInput.min,
        newQuantityInput.max || null,
        newQuantityInput.step
      );
    }

    // Check if quantity rules are appearing/disappearing (causes layout shift)
    const quantityRules = this.refs.quantityRules;
    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    if (isQuantityRulesChanging && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const currentProductFormButtons = this.refs.productFormButtons;
      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (currentProductFormButtons && newProductFormButtons) {
        morph(currentProductFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (newQuantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          newQuantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          newQuantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      const quantityLabel = this.querySelector('.quantity-label');
      const newQuantityLabel = event.detail.data.html.querySelector('.quantity-label');
      this.#morphOrUpdateElement(quantityLabel, newQuantityLabel, quantitySelector);

      const addToCartButton = this.querySelector('[ref="addToCartButtonContainer"]');
      this.#morphOrUpdateElement(quantityRules, newQuantityRules, addToCartButton);
    }

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();

    if (this.#isCustomIndividualConfigurator()) {
      this.#updateIndividualMinimumQuantity();
    }
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };

  /**
   * Checks if this product form is the custom-caps configurator
   * @returns {boolean}
   */
  #isCustomConfigurator() {
    return this.dataset.templateSuffix === 'custom-caps';
  }

  /**
   * Checks if this product form is the individual custom-caps configurator
   * @returns {boolean}
   */
  #isCustomIndividualConfigurator() {
    return this.dataset.templateSuffix === 'custom-caps-individual';
  }

  /**
   * Checks if this product is a stock or new production product (by handle suffix)
   * @returns {boolean}
   */
  #isStockOrNewProduction() {
    const handle = this.dataset.productHandle || '';
    return handle.includes('-stock') || handle.includes('-new-production');
  }

  /**
   * Initializes minimum quantity for stock/new production products (without full configurator logic)
   */
  #initMinimumQuantityOnly() {
    // Check for data-custom-minimum attribute on the quantity field
    const quantityField = /** @type {HTMLElement | null} */ (this.querySelector('.product-form__field--quantity'));
    const customMinimum = quantityField?.dataset.customMinimum;
    
    if (customMinimum) {
      const minQty = parseInt(customMinimum);
      // Update minimum quantity after component is fully initialized
      // Try multiple timing strategies to ensure component is ready
      requestAnimationFrame(() => this.#enforceMinimumQuantity(minQty));
      setTimeout(() => this.#enforceMinimumQuantity(minQty), 100);
      setTimeout(() => this.#enforceMinimumQuantity(minQty), 300);
    }

    // Add tier change listener to redirect between stock and new-production products
    const tierSelect = /** @type {HTMLSelectElement | null} */ (this.querySelector('[data-tier-select]'));
    if (tierSelect) {
      tierSelect.addEventListener('change', this.#onStockNewProductionTierChange);
    }
  }

  /**
   * Handles Production Tier change for stock/new-production products by redirecting
   */
  #onStockNewProductionTierChange = () => {
    const tierSelect = /** @type {HTMLSelectElement | null} */ (this.querySelector('[data-tier-select]'));
    if (!tierSelect) return;

    const selectedTier = tierSelect.value;
    const currentHandle = this.dataset.productHandle || '';

    if (!currentHandle) return;

    // If the selected tier is a different product handle, redirect directly
    if (selectedTier && selectedTier !== currentHandle) {
      window.location.href = `/products/${selectedTier}`;
      return;
    }

    // Fallback: Build the new handle by replacing the tier suffix
    let newHandle;
    const isStockTier = selectedTier.includes('-stock');
    const isNewProductionTier = selectedTier.includes('-new-production');

    if (isStockTier) {
      newHandle = currentHandle.replace('-new-production', '-stock');
    } else if (isNewProductionTier) {
      newHandle = currentHandle.replace('-stock', '-new-production');
    } else {
      return;
    }

    // Redirect to the new product page
    window.location.href = `/products/${newHandle}`;
  };

  /**
   * Enforces minimum quantity by directly manipulating DOM elements
   * @param {number} minQty - The minimum quantity to enforce
   */
  #enforceMinimumQuantity(minQty) {
    const quantityInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('.product-form__field--quantity input[type="number"]')
    );
    const minusBtn = /** @type {HTMLButtonElement | null} */ (
      this.querySelector('.product-form__field--quantity .quantity-minus')
    );
    
    if (quantityInput) {
      quantityInput.min = minQty.toString();
      if (parseInt(quantityInput.value) < minQty) {
        quantityInput.value = minQty.toString();
      }
    }
    
    if (minusBtn && quantityInput) {
      minusBtn.disabled = parseInt(quantityInput.value) <= minQty;
    }
  }

  /**
   * Initializes wholesale quantity minimums from the wholesale app pricing table
   * Only applies to default product template
   */
  #initWholesaleQuantityMinimums() {
    // Wholesale app table may be dynamically loaded, so try multiple times
    const checkForTable = () => {
      const table = document.getElementById('wsaio-volume-discount--table');
      if (table) {
        this.#updateWholesaleQuantityMinimums();
        return true;
      }
      return false;
    };

    // Try immediately
    if (checkForTable()) return;

    // Try after a short delay (for dynamic loading)
    setTimeout(() => checkForTable(), 100);
    setTimeout(() => checkForTable(), 500);
    setTimeout(() => checkForTable(), 1000);

    // Set up MutationObserver to watch for table being added to DOM
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node instanceof HTMLElement) {
            // Check if the added node is the table or contains it
            if (node.id === 'wsaio-volume-discount--table' || 
                node.querySelector('#wsaio-volume-discount--table')) {
              this.#updateWholesaleQuantityMinimums();
              observer.disconnect();
              return;
            }
          }
        }
      }
    });

    // Start observing the document body for changes
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Stop observing after 5 seconds to avoid memory leaks
    setTimeout(() => observer.disconnect(), 5000);
  }

  /**
   * Updates quantity selector with min/max from wholesale app pricing table
   */
  #updateWholesaleQuantityMinimums() {
    const table = document.getElementById('wsaio-volume-discount--table');
    // console.log('[Wholesale] Looking for table...', table);
    if (!table) return;

    const rows = table.querySelectorAll('tbody tr');
    // console.log('[Wholesale] Found rows:', rows.length);
    if (rows.length === 0) return;

    let minQuantity = 1;
    let maxQuantity = /** @type {number | null} */ (null);
    let lowestPrice = '';
    let lowestPriceValue = Infinity;

    rows.forEach(row => {
      const tds = row.querySelectorAll('td');
      const qtyText = tds[0]?.textContent || '';
      const priceText = tds[1]?.textContent || '';
      const qty = parseInt(qtyText) || 0;
      const priceString = priceText.replace(/[^0-9.]/g, '');
      const priceValue = parseFloat(priceString) || Infinity;

      if (qty > 0) {
        if (qty < minQuantity) minQuantity = qty;
        if (maxQuantity === null || qty > maxQuantity) maxQuantity = qty;
      }

      if (priceValue < lowestPriceValue) {
        lowestPriceValue = priceValue;
        lowestPrice = priceString;
      }
    });

    // console.log('[Wholesale] Min quantity:', minQuantity);
    // console.log('[Wholesale] Max quantity:', maxQuantity);
    // console.log('[Wholesale] Lowest price:', lowestPrice);

    // Update wholesale lowest price display
    const wholesalePriceDisplay = document.querySelector('[data-wholesale-lowest-price] [data-wholesale-price-value]');
    if (wholesalePriceDisplay && lowestPrice) {
      wholesalePriceDisplay.textContent = '$' + lowestPrice;
      // console.log('[Wholesale] Updated lowest price display');
    }

    const quantitySelector = /** @type {any} */ (document.querySelector('quantity-selector-component'));
    // console.log('[Wholesale] Quantity selector:', quantitySelector);
    if (quantitySelector?.updateConstraints) {
      quantitySelector.updateConstraints(
        minQuantity.toString(),
        maxQuantity?.toString() ?? null,
        '1'
      );
      // console.log('[Wholesale] Updated constraints');

      // Only set the value to minimum on first initialization
      // Subsequent calls should not reset the user's input
      const currentValue = parseInt(quantitySelector.getValue?.()) || 1;
      // console.log('[Wholesale] Current value:', currentValue);
      if (currentValue < minQuantity) {
        quantitySelector.setValue(minQuantity.toString());
        // console.log('[Wholesale] Set value to minimum:', minQuantity);
      }
    }
  }

  /**
   * Determines whether this configurator is New Production
   * @returns {boolean}
   */
  #isNewProduction() {
    const tierSelect = /** @type {HTMLSelectElement | null} */ (this.querySelector('[data-tier-select]'));
    const handle = this.dataset.productHandle || '';
    return tierSelect?.value === 'custom-caps-new-production'
      || handle.includes('-new-production');
  }

  /**
   * Updates the minimum quantity for individual custom caps based on production tier
   * Stock products: minimum 48, New production: minimum 250
   * @param {boolean} resetQuantity - Whether to reset the quantity to the new minimum (for tier switches)
   */
  #updateIndividualMinimumQuantity(resetQuantity = false) {
    const handle = this.dataset.productHandle || '';
    const isNewProduction = handle.includes('-new-production');
    const isStock = handle.includes('-stock');
    const minimumQuantity = isNewProduction ? 250 : 48;
    const maximumQuantity = isStock ? 250 : null;
    
    const quantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
    
    if (quantitySelector?.updateConstraintsWithSnapping) {
      const currentValues = quantitySelector.getCurrentValues?.() || {};
      const effectiveMaximum = isStock ? (currentValues.max || maximumQuantity) : currentValues.max;
      quantitySelector.updateConstraintsWithSnapping(
        minimumQuantity.toString(),
        effectiveMaximum?.toString() ?? null,
        '1'
      );
    }
    
    // Also update the quantity input directly if it exists
    const quantityInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );
    if (quantityInput) {
      quantityInput.min = minimumQuantity.toString();
      if (maximumQuantity) quantityInput.max = maximumQuantity.toString();
      // Reset to the new minimum quantity only when switching tiers
      if (resetQuantity) {
        quantityInput.value = minimumQuantity.toString();
      } else {
        // Clamp value within the allowed range for normal page loads
        const currentValue = parseInt(quantityInput.value) || minimumQuantity;
        const clampedValue = Math.max(minimumQuantity, Math.min(maximumQuantity ?? Infinity, currentValue));
        if (currentValue !== clampedValue) {
          quantityInput.value = clampedValue.toString();
        }
      }
    }
    
    // Ensure button states are updated to reflect new minimum
    if (quantitySelector?.updateButtonStates) {
      quantitySelector.updateButtonStates();
    }
  }

  /**
   * Shows a prompt to switch to New Production when the quantity reaches the Stock max.
   */
  #initTierSwitchPrompt() {
    if (!this.#isCustomIndividualConfigurator()) return;

    const handle = this.dataset.productHandle || '';
    if (!handle.includes('-stock') || handle.includes('-new-production')) return;

    const quantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
    const quantityInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );
    const tierSelect = /** @type {HTMLSelectElement | null} */ (this.querySelector('[data-tier-select]'));
    const message = this.querySelector('[data-tier-switch-message]');
    const switchButton = this.querySelector('[data-tier-switch="new-production"]');

    if (!message || !switchButton || !tierSelect || !quantityInput) return;

    const updateMessage = () => {
      const currentValues = quantitySelector?.getCurrentValues?.() || {};
      const value = currentValues.value || 0;
      const effectiveMax = quantitySelector?.getEffectiveMax?.() ?? currentValues.max ?? Infinity;
      message.classList.toggle('hidden', value < effectiveMax);
    };

    this.addEventListener('quantity-selector:update', updateMessage);
    quantityInput.addEventListener('input', updateMessage);
    quantityInput.addEventListener('change', updateMessage);

    switchButton.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();

      // Use the related tier product handle from metafield if available
      const relatedTierHandle = this.dataset.relatedTierProductHandle;
      if (relatedTierHandle) {
        tierSelect.value = relatedTierHandle;
        this.#onIndividualConfiguratorChange();
      } else {
        // Fallback: construct the new production handle by replacing -stock with -new-production
        const currentHandle = this.dataset.productHandle || '';
        const newProductionHandle = currentHandle.replace(/-stock(?=-|$)/, '-new-production');
        tierSelect.value = newProductionHandle;
        this.#onIndividualConfiguratorChange();
      }
    });

    updateMessage();
  }

  /**
   * Initializes the custom cap-style configurator
   */
  #initCustomConfigurator() {
    const tierSelect = this.querySelector('[data-tier-select]');
    const capStyleSelect = /** @type {HTMLSelectElement | null} */ (this.querySelector('[data-cap-style-select]'));
    const nfcInputs = this.querySelectorAll('[data-nfc-input]');
    const capStyleCapsules = this.querySelectorAll('[data-cap-style-capsule]');

    tierSelect?.addEventListener('change', this.#onTierChange);
    capStyleSelect?.addEventListener('change', this.#onConfiguratorChange);
    nfcInputs.forEach((input) => {
      input.addEventListener('change', this.#onConfiguratorChange);
      input.addEventListener('change', this.#onNfcChange);
    });

    capStyleCapsules.forEach((input) => {
      input.addEventListener('change', () => {
        if (capStyleSelect && input instanceof HTMLInputElement) {
          capStyleSelect.value = input.value;
          capStyleSelect.dispatchEvent(new Event('change'));
        }
      });
    });

    this.#onTierChange();
  }

  /**
   * Initializes the individual custom cap configurator
   */
  async #initCustomIndividualConfigurator() {
    const tierSelect = /** @type {HTMLSelectElement | null} */ (this.querySelector('[data-tier-select]'));
    const nfcInputs = this.querySelectorAll('[data-nfc-input]');
    const handle = this.dataset.productHandle || '';
    const picker = this.querySelector('[data-dynamic-variant-picker]');
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    const skuInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="sku"]'));

    if (tierSelect) {
      if (handle.includes('-new-production')) {
        // Try to find an option with -new-production suffix, otherwise use default
        const newProdOption = Array.from(tierSelect.options).find(opt => opt.value.includes('-new-production'));
        tierSelect.value = newProdOption ? newProdOption.value : 'custom-caps-new-production';
      } else if (handle.includes('-stock')) {
        // Try to find an option with -stock suffix, otherwise use default
        const stockOption = Array.from(tierSelect.options).find(opt => opt.value.includes('-stock'));
        tierSelect.value = stockOption ? stockOption.value : 'custom-caps-stock';
      }
    }

    tierSelect?.addEventListener('change', this.#onIndividualConfiguratorChange);
    nfcInputs.forEach((input) => {
      input.addEventListener('change', this.#onNfcChange);
    });

    // Add color swatch change listener for image swapping
    const colorInputs = this.querySelectorAll('.product-color-swatches__input');
    colorInputs.forEach((input) => {
      input.addEventListener('change', this.#onColorChange);
    });

    this.#checkIndividualTierAvailability();
    this.#loadInitialColors();

    // Update minimum quantity after component is fully initialized
    // Try multiple timing strategies to ensure component is ready
    requestAnimationFrame(() => this.#updateIndividualMinimumQuantity());
    setTimeout(() => this.#updateIndividualMinimumQuantity(), 100);
    setTimeout(() => this.#updateIndividualMinimumQuantity(), 300);

    this.#initTierSwitchPrompt();

    if (!handle || !picker || !variantIdInput) return;

    const product = await this.#loadProduct(handle);
    if (!product || !this.isConnected) return;

    const getSelectedValues = () => product.options.map((/** @type {string} */ option, /** @type {number} */ index) => {
      const radio = /** @type {HTMLInputElement | null} */ (
        picker.querySelector(`input[data-option-index="${index}"]:checked`)
      );
      if (radio) return radio.value;

      const select = /** @type {HTMLSelectElement | null} */ (
        picker.querySelector(`select[data-option-index="${index}"]`)
      );
      return select?.value || '';
    });

    const updateVariant = () => {
      const selectedValues = getSelectedValues();
      const variant = /** @type {any[]} */ (product.variants || []).find((candidate) =>
        candidate.options.every((/** @type {string} */ option, /** @type {number} */ index) => option === selectedValues[index])
      );
      if (!variant) {
        console.warn('[individual variant picker] no match — selectedValues:', selectedValues);
        return;
      }

      variantIdInput.value = variant.id.toString();
      if (skuInput) skuInput.value = variant.sku || '';
      this.dataset.productId = product.id.toString();

      const skuDisplay = this.closest('.shopify-section')?.querySelector('[data-sku-display]');
      const skuBlock = skuDisplay?.closest('product-sku');
      if (skuDisplay && skuBlock instanceof HTMLElement) {
        const prefix = skuBlock.dataset.skuPrefix || '';
        skuDisplay.textContent = variant.sku ? `${prefix}${variant.sku}` : prefix.trim();
        skuBlock.toggleAttribute('hidden', !variant.sku && skuBlock.dataset.hideWhenBlank === 'true');
      }

      this.dispatchEvent(
        new CustomEvent('sku:update', {
          bubbles: true,
          detail: { productId: product.id.toString(), sku: variant.sku || '' },
        })
      );
      this.#updatePriceDisplay(variant, product);
    };

    picker.querySelectorAll('select, input[type="radio"]').forEach((input) => {
      input.addEventListener('change', updateVariant);
    });

    picker.querySelectorAll('input[type="radio"]').forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      const optionIndex = Number.parseInt(input.dataset.optionIndex || '', 10);
      if (product.options[optionIndex]?.name === 'NFC Chip') {
        input.addEventListener('change', this.#onNfcChange);
      }
    });

    // Restore selections carried over from a Stock -> New Production switch.
    const tierSwitchStateRaw = sessionStorage.getItem('tierSwitchState');
    if (tierSwitchStateRaw) {
      try {
        const tierSwitchState = JSON.parse(tierSwitchStateRaw);
        if (tierSwitchState?.handle === this.dataset.productHandle) {
          const stateQuantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
          const stateQuantityInput = /** @type {HTMLInputElement | null} */ (
            this.querySelector('quantity-selector-component input[ref="quantityInput"]')
          );

          if (tierSwitchState.options && picker) {
            Object.entries(tierSwitchState.options).forEach(([index, value]) => {
              const radio = picker.querySelector(`input[data-option-index="${index}"][value="${value}"]`);
              if (radio instanceof HTMLInputElement) radio.checked = true;

              const select = picker.querySelector(`select[data-option-index="${index}"]`);
              if (select instanceof HTMLSelectElement) select.value = String(value);
            });
          }

          // Select first option as default if nothing is selected
          picker?.querySelectorAll('select[data-option-index]').forEach((select) => {
            if (select instanceof HTMLSelectElement && !select.value) {
              const firstOption = select.querySelector('option');
              if (firstOption) {
                select.value = firstOption.value;
                select.dispatchEvent(new Event('change'));
              }
            }
          });

          // Reset quantity to the new minimum when switching tiers
          this.#updateIndividualMinimumQuantity(true);

          sessionStorage.removeItem('tierSwitchState');
        }
      } catch (error) {
        console.error('[tier-switch] Failed to restore state:', error);
        sessionStorage.removeItem('tierSwitchState');
      }
    }

    updateVariant();
  }

  /**
   * Loads initial colors on page load without redirecting
   */
  async #loadInitialColors() {
    const currentHandle = this.dataset.productHandle || '';
    if (!currentHandle) return;

    const colorData = await this.#loadProductColors(currentHandle);
    this.#renderColorSwatches(colorData);
  }

  /**
   * Handles Production Tier change by filtering cap styles and reloading the sub-product
   */
  #onTierChange = () => {
    this.#filterCapStyleOptions();
    this.#onConfiguratorChange();
  };

  /**
   * Handles NFC change by re-rendering color swatches with filtering
   */
  #onNfcChange = async () => {
    const currentHandle = this.dataset.productHandle || '';
    if (!currentHandle) return;

    const colorData = await this.#loadProductColors(currentHandle);
    this.#renderColorSwatches(colorData);
  };

  /**
   * Handles color change by updating the main product image
   * @param {Event} event
   */
  #onColorChange = (event) => {
    // Only apply color image swapping for custom-caps-individual template
    if (!this.#isCustomIndividualConfigurator()) return;
    
    if (!(event.target instanceof HTMLInputElement)) return;

    const colorInput = event.target;
    const selectedColor = colorInput.value;
    const imageUrl = colorInput.dataset.colorImage;

    if (!imageUrl) {
      return;
    }

    // Find the main product image in the media gallery
    /** @type {import('./media-gallery').MediaGallery | null} */
    let mediaGallery = /** @type {import('./media-gallery').MediaGallery | null} */ (this.closest('product-information')?.querySelector('media-gallery') ?? null);

    // If not found, try broader search
    if (!mediaGallery) {
      mediaGallery = /** @type {import('./media-gallery').MediaGallery | null} */ (document.querySelector('media-gallery') ?? null);
    }
    
    if (!mediaGallery) {
      return;
    }

    // Sync the slideshow to the slide whose image matches the selected color
    const slideshow = mediaGallery.refs.slideshow;
    if (!slideshow) {
      return;
    }

    // Match by normalized image filename (width params and extensions differ)
    /** @param {string} url */
    const normalizeImageName = (url) => {
      try {
        const { pathname } = new URL(url, window.location.href);
        const filename = pathname.substring(pathname.lastIndexOf('/') + 1);
        // Strip Shopify width suffixes like _800x / _3840x, then strip extension
        return filename.replace(/_\d+x(?=\.|$)/, '').replace(/\.[^.]+$/, '');
      } catch {
        return '';
      }
    };

    const colorImageName = normalizeImageName(imageUrl);
    const slides = slideshow.refs.slides ?? [];
    const colorImageIndex = slides.findIndex((slide) => {
      const img = slide.querySelector('img');
      if (!img?.src) return false;
      return normalizeImageName(img.src) === colorImageName;
    });

    // Mark media gallery as color-override to prevent variant updates from replacing it
    mediaGallery.setAttribute('data-color-override', selectedColor);

    if (colorImageIndex !== -1) {
      // Updates current index, thumbnail aria-selected, scroll, and centers thumbnail
      slideshow.select(colorImageIndex, event);
      return;
    }

    // Fallback: color image is not a product-media slide (e.g. external /files/ URL)
    const activeSlideImage = mediaGallery.querySelector('slideshow-slide[aria-hidden="false"] img');
    const targetImage = /** @type {HTMLImageElement | null} */ (
      activeSlideImage || mediaGallery.querySelector('slideshow-slide:first-child img, .product-media img')
    );

    if (!targetImage) {
      return;
    }

    // Force a reload by adding a timestamp
    const timestamp = new Date().getTime();
    const newImageUrl = imageUrl + (imageUrl.includes('?') ? '&' : '?') + 't=' + timestamp;

    // Add onerror handler to fallback from .webp to .png
    targetImage.onerror = () => {
      if (newImageUrl.includes('.webp')) {
        const pngUrl = newImageUrl.replace('.webp', '.png');
        targetImage.src = pngUrl;
      }
    };

    targetImage.src = newImageUrl;
    targetImage.srcset = ''; // Clear srcset to avoid conflicts

    // Also update data_max_resolution attribute if it exists
    if (targetImage.dataset.maxResolution) {
      targetImage.dataset.maxResolution = newImageUrl;
    }
  };

  /**
   * Filters cap style options based on the selected Production Tier
   * 04-CF is not available for New Production
   */
  #filterCapStyleOptions() {
    const capStyleSelect = /** @type {HTMLSelectElement | null} */ (this.querySelector('[data-cap-style-select]'));
    if (!capStyleSelect) return;

    const isNewProduction = this.#isNewProduction();
    let hasVisibleSelection = false;

    Array.from(capStyleSelect.options).forEach((option) => {
      const isExcluded = isNewProduction && option.value === '04-CF';
      option.hidden = isExcluded;
      if (!isExcluded && option.value === capStyleSelect.value) {
        hasVisibleSelection = true;
      }
    });

    this.querySelectorAll('[data-cap-style-capsule]').forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      const isExcluded = isNewProduction && input.value === '04-CF';
      const capsule = input.closest('.product-cap-style__capsule');
      if (capsule instanceof HTMLElement) {
        capsule.style.display = isExcluded ? 'none' : '';
      }
      if (isExcluded && input.checked) {
        input.checked = false;
        hasVisibleSelection = false;
      } else if (!isExcluded && input.value === capStyleSelect.value) {
        hasVisibleSelection = true;
      }
    });

    if (!hasVisibleSelection) {
      const firstVisible = Array.from(capStyleSelect.options).find((option) => !option.hidden);
      if (firstVisible) {
        capStyleSelect.value = firstVisible.value;
        const firstCapsule = /** @type {HTMLInputElement | null} */ (
          this.querySelector(`[data-cap-style-capsule][value="${firstVisible.value}"]`)
        );
        if (firstCapsule) firstCapsule.checked = true;
      }
    }
  }

  /**
   * Handles cap style or NFC change by loading the matching sub-product
   */
  #onConfiguratorChange = async () => {
    const handle = this.#getProductHandle();
    if (!handle) return;

    const [product, colorData] = await Promise.all([
      this.#loadProduct(handle),
      this.#loadProductColors(handle),
    ]);
    if (!product) return;

    this.#updateNfcTabState();
    this.#renderColorSwatches(colorData);
    this.#renderVariantPicker(product);
  };

  /**
   * Handles Production Tier or NFC change by redirecting to the matching individual product
   */
  #onIndividualConfiguratorChange = async () => {
    const handle = this.#getIndividualProductHandle();
    if (!handle) return;

    // Persist current quantity and variant selections so they survive the redirect.
    const quantityInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );
    const picker = this.querySelector('[data-dynamic-variant-picker]');
    /** @type {Record<string, string>} */
    const options = {};
    picker?.querySelectorAll('input[data-option-index]:checked, select[data-option-index]').forEach((input) => {
      if (input instanceof HTMLInputElement || input instanceof HTMLSelectElement) {
        const index = input.dataset.optionIndex || '';
        if (index) options[index] = input.value;
      }
    });

    sessionStorage.setItem(
      'tierSwitchState',
      JSON.stringify({
        handle,
        options,
      })
    );

    // Redirect to the new product page
    window.location.href = `/products/${handle}`;
  };

  /**
   * Builds the sub-product handle from the selected cap style and NFC state
   * @returns {string}
   */
  #getProductHandle() {
    const tierSelect = /** @type {HTMLSelectElement | null} */ (this.querySelector('[data-tier-select]'));
    const capStyleSelect = /** @type {HTMLSelectElement | null} */ (this.querySelector('[data-cap-style-select]'));
    const nfcInput = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-nfc-input]:checked'));

    if (!capStyleSelect || !nfcInput) return '';

    const capStyle = capStyleSelect.value;
    const nfc = nfcInput.value === 'with-nfc';
    const base = tierSelect?.value || this.dataset.productHandle || '';
    const stylePart = capStyle.toLowerCase().replace(/\s+/g, '-');
    const nfcPart = nfc ? '-nfc' : '';

    return `${base}-${stylePart}${nfcPart}`;
  }

  /**
   * Builds the product handle from the selected Production Tier, cap style slug, and NFC state
   * @returns {string}
   */
  #getIndividualProductHandle() {
    const tierSelect = /** @type {HTMLSelectElement | null} */ (this.querySelector('[data-tier-select]'));
    const currentHandle = this.dataset.productHandle || '';
    const selectedBase = tierSelect?.value;

    if (!currentHandle) return '';
    if (!selectedBase) return currentHandle;

    // If the selected tier is a different product handle, use it directly
    if (selectedBase && selectedBase !== currentHandle) {
      return selectedBase;
    }

    // Use the related tier product handle from metafield if available
    const relatedTierHandle = this.dataset.relatedTierProductHandle;
    if (relatedTierHandle) {
      return relatedTierHandle;
    }

    // Fallback: Build the new handle by replacing the tier suffix
    const isStockTier = selectedBase.includes('-stock');
    const tier = isStockTier ? 'stock' : 'new-production';
    // Handle both formats: custom-caps-stock and 01-cf-classic-fit-performance-icon-stock
    return currentHandle.replace(/-(stock|new-production)(?=-|$)/, `-${tier}`);
  }

  /**
   * Fetches a product by handle via the Shopify product JSON API
   * @param {string} handle
   * @returns {Promise<any | null>}
   */
  async #loadProduct(handle) {
    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) throw new Error(`Failed to load product: ${handle}`);
      return await response.json();
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  /**
   * Fetches colors from the product's custom.colors metafield via the colors view
   * @param {string} handle
   * @returns {Promise<{ colors: string[], colorImages: Record<string, string> }>}
   */
  async #loadProductColors(handle) {
    try {
      const response = await fetch(`/products/${handle}?view=colors`);
      if (!response.ok) return { colors: [], colorImages: /** @type {Record<string, string>} */ ({}) };
      const text = await response.text();
      const parsed = JSON.parse(text);
      if (!parsed) return { colors: [], colorImages: /** @type {Record<string, string>} */ ({}) };
      
      const colors = String(parsed).split('\n').map((c) => c.trim()).filter(Boolean);
      
      // Get color-to-image mapping from the data attribute (passed from Liquid)
      const colorImagesData = this.dataset.colorImages;
      let colorImages = /** @type {Record<string, string>} */ ({});
      if (colorImagesData) {
        try {
          const colorImageFilenames = JSON.parse(colorImagesData);
          
          // Fetch product data to get images for filename matching
          const productResponse = await fetch(`/products/${handle}.js`);
          const productData = await productResponse.json();
          
          // Convert filenames to URLs by matching with product images
          for (const [colorName, filename] of Object.entries(colorImageFilenames)) {
            if (filename) {
              // Check if it's already a full URL
              if (filename.startsWith('http')) {
                colorImages[colorName] = filename;
              } else {
                // Try to find image in product media by filename
                const matchingImage = productData.images?.find((/** @type {{ src?: string; alt?: string }} */ img) => {
                  const imgSrc = img.src || '';
                  const imgAlt = img.alt || '';
                  return imgSrc.includes(filename) || imgAlt.includes(filename);
                });
                
                if (matchingImage) {
                  colorImages[colorName] = matchingImage.src;
                } else {
                  // Fallback: construct relative path URL format
                  const fallbackUrl = `/cdn/shop/files/${filename}.webp`;
                  colorImages[colorName] = fallbackUrl;
                }
              }
            }
          }
        } catch (e) {
          console.error('[color-image] Failed to parse color images data:', e);
        }
      }
      
      return { colors, colorImages };
    } catch (error) {
      console.error(error);
      return { colors: [], colorImages: /** @type {Record<string, string>} */ ({}) };
    }
  }

  /**
   * Renders color swatches from the custom.colors metafield
   * @param {{colors: string[], colorImages: Record<string, string>}} colorData
   */
  #renderColorSwatches(colorData) {
    const container = /** @type {HTMLElement | null} */ (this.querySelector('[data-color-list]'));
    const valueDisplay = /** @type {HTMLElement | null} */ (this.querySelector('[data-color-value]'));
    if (!container) return;

    const colorTags = colorData.colors || [];
    const colorImages = colorData.colorImages || {};

    if (colorTags.length === 0) {
      container.innerHTML = '<p class="product-color-swatches__placeholder">No colors available</p>';
      if (valueDisplay) valueDisplay.textContent = '';
      return;
    }

    // Filter colors to Black only if NFC is enabled and product is 01-cf, 02-sf, or 03-r-sf
    // Check both NFC tab radio input and NFC Chip variant radio input
    const nfcInput = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-nfc-input]:checked'));
    
    // Find the NFC Chip variant option specifically
    const nfcChipOption = Array.from(
      /** @type {NodeListOf<HTMLInputElement>} */ (this.querySelectorAll('.product-dynamic-variant-picker__radio-input'))
    ).find(input => input.value.includes('NFC Chip'));
    const nfcVariantInput = /** @type {HTMLInputElement | null} */ (nfcChipOption);
    
    let hasNfc = false;
    if (nfcInput?.value === 'with-nfc') {
      hasNfc = true;
    } else if (nfcVariantInput?.checked && nfcVariantInput?.value.includes('With')) {
      hasNfc = true;
    }
    
    const currentHandle = this.dataset.productHandle || '';
    const isStock = currentHandle.toLowerCase().includes('-stock');
    const nfcRestrictedProducts = ['01-cf', '02-sf', '03-r-sf'];
    
    let filteredColors = colorTags;
    if (hasNfc) {
      const isRestrictedProduct = isStock && nfcRestrictedProducts.some(prefix => 
        currentHandle.toLowerCase().startsWith(prefix)
      );
      if (isRestrictedProduct) {
        filteredColors = colorTags.filter(color => color.toLowerCase() === 'black');
      }
    }

    if (filteredColors.length === 0) {
      container.innerHTML = '<p class="product-color-swatches__placeholder">No colors available</p>';
      if (valueDisplay) valueDisplay.textContent = '';
      return;
    }

    // Get currently selected color to preserve it after re-render (must be before clearing the container)
    const currentlySelectedColor = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[name="properties[Color]"]:checked')
    )?.value;

    container.innerHTML = '';

    filteredColors.forEach((color, index) => {
      const colorValue = ProductFormComponent.#colorMap[color] || ProductFormComponent.#colorMap[color.trim()] || '#CCCCCC';
      const isCamo = colorValue === 'camo';
      const isLight = !isCamo && this.#isLightColor(colorValue);
      const inlineStyle = isCamo ? '' : `style="background-color: ${colorValue};"`;
      const extraClasses = [
        isCamo ? 'product-color-swatches__swatch--camo' : '',
        isLight ? 'product-color-swatches__swatch--light' : '',
      ].filter(Boolean).join(' ');

      // Get image URL for this color
      const colorImageUrl = colorImages[color] || '';

      // Preserve the currently selected color, otherwise default to first
      const isChecked = currentlySelectedColor === color || (index === 0 && !currentlySelectedColor);

      const label = document.createElement('label');
      label.className = 'product-color-swatches__item';
      label.innerHTML = `
        <input type="radio" name="properties[Color]" value="${color}" ${isChecked ? 'checked' : ''} class="product-color-swatches__input" data-color-image="${colorImageUrl}">
        <span class="product-color-swatches__swatch ${extraClasses}" ${inlineStyle} aria-label="${color}"></span>
        <span class="product-color-swatches__tooltip">${color}</span>
      `;
      container.appendChild(label);
    });

    // Display the preserved color if it's still in the filtered list, otherwise show the first
    const displayColor = (currentlySelectedColor && filteredColors.includes(currentlySelectedColor))
      ? currentlySelectedColor
      : filteredColors[0] || '';
    if (valueDisplay) valueDisplay.textContent = displayColor;

    // Re-attach color change event listeners for image swapping
    container.querySelectorAll('input[type="radio"]').forEach((input) => {
      input.addEventListener('change', (event) => {
        if (valueDisplay && input instanceof HTMLInputElement) valueDisplay.textContent = input.value;
        // Trigger image change
        this.#onColorChange(event);
      });
    });

    // Reveal the list now that the correct filtered colors have been rendered
    container.style.opacity = '1';
  }

  /**
   * Renders a dynamic variant picker for Decoration and Sticker
   * @param {any} product
   */
  #renderVariantPicker(product) {
    const container = this.querySelector('[data-dynamic-variant-picker]');
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!container || !variantIdInput) return;

    container.innerHTML = '';

    const isNewProduction = this.#isNewProduction();
    const options = /** @type {{name: string, position: number, values: string[]}[]} */ (product.options || []);

    options.forEach((option, optionIndex) => {
      const wrapper = document.createElement('div');
      wrapper.className = 'product-form__field product-dynamic-variant-picker__option';

      const labelEl = document.createElement('label');
      labelEl.className = 'product-form__field-label';
      labelEl.textContent = option.name === 'Sticker' ? 'Custom Sticker' : option.name;
      wrapper.appendChild(labelEl);

      const uniqueValues = [...new Set(option.values)];

      if (option.name === 'Sticker' || option.name === 'Custom Sticker' || option.name === 'NFC Chip') {
        const radioWrapper = document.createElement('div');
        radioWrapper.className = 'product-dynamic-variant-picker__radios';
        radioWrapper.dataset.optionIndex = optionIndex.toString();

        uniqueValues.forEach((value, i) => {
          const radioLabel = document.createElement('label');
          radioLabel.className = 'product-dynamic-variant-picker__radio-option';

          const input = document.createElement('input');
          input.type = 'radio';
          input.name = `variant-option-${optionIndex}`;
          input.value = value;
          input.dataset.optionIndex = optionIndex.toString();
          input.className = 'product-dynamic-variant-picker__radio-input';
          if (option.name === 'NFC Chip') {
            if (value === 'Without NFC Chip' || i === 0) input.checked = true;
          } else {
            if (i === 0) input.checked = true;
          }

          // Add event listener for NFC Chip changes to re-render colors
          if (option.name === 'NFC Chip') {
            input.addEventListener('change', this.#onNfcChange);
          }

          const span = document.createElement('span');
          span.textContent = this.#formatOptionLabel(option.name, value, isNewProduction);

          radioLabel.appendChild(input);
          radioLabel.appendChild(span);
          radioWrapper.appendChild(radioLabel);
        });

        wrapper.appendChild(radioWrapper);
      } else {
        const inputWrapper = document.createElement('div');
        inputWrapper.className = 'product-form__field-input';

        const select = document.createElement('select');
        select.className = 'product-dynamic-variant-picker__select';
        select.dataset.optionIndex = optionIndex.toString();

        uniqueValues.forEach((value) => {
          const optionEl = document.createElement('option');
          optionEl.value = value;
          optionEl.textContent = this.#formatOptionLabel(option.name, value, isNewProduction);
          select.appendChild(optionEl);
        });

        inputWrapper.appendChild(select);
        wrapper.appendChild(inputWrapper);
      }

      container.appendChild(wrapper);
    });

    const getSelectedValues = () => {
      return options.map((option, i) => {
        if (option.name === 'Sticker' || option.name === 'Custom Sticker' || option.name === 'NFC Chip') {
          const checked = /** @type {HTMLInputElement | null} */ (
            container.querySelector(`input[type="radio"][data-option-index="${i}"]:checked`)
          );
          return checked?.value || '';
        }
        const select = /** @type {HTMLSelectElement | null} */ (
          container.querySelector(`select[data-option-index="${i}"]`)
        );
        return select?.value || '';
      });
    };

    const updateVariantId = () => {
      const selectedValues = getSelectedValues();
      const variant = /** @type {any[]} */ (product.variants || []).find((/** @type {any} */ v) =>
        v.options.every((/** @type {string} */ opt, /** @type {number} */ i) => opt === selectedValues[i])
      );
      if (variant) {
        variantIdInput.value = variant.id.toString();
        this.dataset.productId = product.id.toString();
        this.#updatePriceDisplay(variant, product);
      } else {
        console.warn('[variant picker] no match — selectedValues:', selectedValues, 'available:', (product.variants || []).map((/** @type {any} */ v) => v.options));
      }
    };

    container.querySelectorAll('select').forEach((select) => {
      select.addEventListener('change', updateVariantId);
    });

    container.querySelectorAll('input[type="radio"]').forEach((input) => {
      input.addEventListener('change', updateVariantId);
    });

    updateVariantId();
  }

  /**
   * Determines if a hex color is light (needs a dark border for visibility)
   * @param {string} hex
   * @returns {boolean}
   */
  #isLightColor(hex) {
    if (hex === 'camo') return false;
    const clean = hex.replace('#', '');
    const r = parseInt(clean.slice(0, 2), 16);
    const g = parseInt(clean.slice(2, 4), 16);
    const b = parseInt(clean.slice(4, 6), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 200;
  }

  /**
   * Formats a price with the store currency
   * @param {string} price
   * @param {string} currency
   * @returns {string}
   */
  #formatPrice(price, currency) {
    const amount = parseFloat(price) / 100;
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
  }

  /**
   * Formats a variant option label; adds lead times for Stock decoration options
   * @param {string} optionName
   * @param {string} value
   * @param {boolean} isNewProduction
   * @returns {string}
   */
  #formatOptionLabel(optionName, value, isNewProduction) {
    if (optionName !== 'Decoration' || isNewProduction) return value;

    /** @type {Record<string, string>} */
    const leadTimes = {
      'Flat Embroidery': '3-7 working days',
      'Puff Embroidery': '3-7 working days',
      'TPU': '7-15 working days',
      'Silicone': '7-15 working days',
      'Woven Patch': '7-15 working days',
      'Embroidery Patch': '7-15 working days',
    };

    const leadTime = leadTimes[value];
    return leadTime ? `${value} (${leadTime})` : value;
  }

  /**
   * Dispatches a VariantUpdateEvent by fetching fresh section HTML for the given variant.
   * product-price listens for this event to update its display.
   * @param {any} variant
   * @param {any} product
   */
  async #updatePriceDisplay(variant, product) {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    this.#priceUpdateAbortController?.abort();
    this.#priceUpdateAbortController = new AbortController();

    const url = `${window.location.pathname}?variant=${variant.id}&section_id=${sectionId}`;
    try {
      const response = await fetch(url, { signal: this.#priceUpdateAbortController.signal });
      if (!response.ok) return;
      const text = await response.text();
      const html = new DOMParser().parseFromString(text, 'text/html');
      this.dataset.productId = product.id.toString();
      this.dispatchEvent(
        new VariantUpdateEvent(variant, this.id || sectionId, {
          html,
          productId: product.id.toString(),
        })
      );
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') return;
      console.error('[updatePriceDisplay]', e);
    }
  }

  /**
   * Disables the With NFC option for cap styles that don't have an NFC product
   */
  #updateNfcTabState() {
    const withNfcInput = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-nfc-input][value="with-nfc"]'));
    const withNfcLabel = withNfcInput?.closest('.product-nfc-tab__option');

    if (!withNfcInput || !withNfcLabel) return;

    let hasNfc = false;

    if (this.#isCustomIndividualConfigurator()) {
      hasNfc = this.dataset.hasNfc === 'true';
    } else {
      const capStyleSelect = /** @type {HTMLSelectElement | null} */ (this.querySelector('[data-cap-style-select]'));
      if (!capStyleSelect) return;
      const selectedOption = capStyleSelect.options[capStyleSelect.selectedIndex];
      hasNfc = selectedOption?.dataset.hasNfc === 'true';
    }

    if (!hasNfc) {
      withNfcInput.disabled = true;
      withNfcLabel.classList.add('product-nfc-tab__option--disabled');
      if (withNfcInput.checked) {
        const withoutNfcInput = this.querySelector('[data-nfc-input][value="without-nfc"]');
        if (withoutNfcInput instanceof HTMLInputElement) withoutNfcInput.checked = true;
      }
    } else {
      withNfcInput.disabled = false;
      withNfcLabel.classList.remove('product-nfc-tab__option--disabled');
    }
  }

  /**
   * Hides unavailable Production Tier options and hides the tier selector if only one option exists
   */
  async #checkIndividualTierAvailability() {
    const tierSelect = /** @type {HTMLSelectElement | null} */ (this.querySelector('[data-tier-select]'));
    const tierField = tierSelect?.closest('.product-form__field--tier');
    const currentHandle = this.dataset.productHandle || '';

    if (!tierSelect || !currentHandle) return;

    const currentTier = currentHandle.includes('-new-production')
      ? 'new-production'
      : currentHandle.includes('-stock')
        ? 'stock'
        : '';
    if (!currentTier) return;

    const alternateTier = currentTier === 'stock' ? 'new-production' : 'stock';
    const alternateHandle = currentHandle.replace(
      /^custom-caps-(stock|new-production)(?=-|$)/,
      `custom-caps-${alternateTier}`
    );

    try {
      const response = await fetch(`/products/${alternateHandle}.js`, { method: 'HEAD' });
      const hasAlternate = response.ok;
      
      // Map tier names to option values
      const tierToOptionValue = {
        'stock': 'custom-caps-stock',
        'new-production': 'custom-caps-new-production'
      };
      
      const alternateOptionValue = tierToOptionValue[alternateTier];
      const alternateOption = tierSelect.querySelector(`option[value="${alternateOptionValue}"]`);
      
      if (alternateOption instanceof HTMLOptionElement) {
        alternateOption.hidden = !hasAlternate;
      }

      // Hide the entire tier selector if only one option is available
      const visibleOptions = Array.from(tierSelect.options).filter(opt => !opt.hidden);
      
      if (visibleOptions.length <= 1 && tierField instanceof HTMLElement) {
        tierField.style.display = 'none';
      } else if (tierField instanceof HTMLElement) {
        tierField.style.display = '';
      }
    } catch (error) {
      console.error(error);
    }
  }
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element | null} */
  source = null;

  /** @type {Element | null} */
  destination = null;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    if (!this.source || !this.destination) return;

    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
